import { useEffect, useState } from "react";
import api, { showApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Pill, Sparkles, Clock, RefreshCw, AlertTriangle, Utensils, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PremiumLock, openUpgrade } from "@/components/PremiumLock";
import AiLoader from "@/components/AiLoader";
import { toast } from "sonner";

const PRIORITY = {
  alta: "bg-destructive text-destructive-foreground",
  media: "bg-accent text-accent-foreground",
  bassa: "bg-secondary text-secondary-foreground",
};

export default function Supplements() {
  const { user } = useAuth();
  const premium = !!user?.is_premium;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/supplements");
    setData(data && data.supplements ? data : null);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/supplements/generate");
      setData(data);
      toast.success("Integratori consigliati");
    } catch (err) {
      showApiError(err);
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="grid sm:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>;

  if (generating) return <Card className="rounded-2xl" data-testid="supplements-generating"><CardContent className="p-0"><AiLoader type="supplements" title="DietFlix sta cercando gli integratori giusti" /></CardContent></Card>;

  if (!data) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="py-16 text-center space-y-4">
          <div className="h-14 w-14 rounded-2xl bg-secondary grid place-items-center mx-auto"><Pill className="h-7 w-7 text-primary" /></div>
          <h3 className="font-heading text-2xl font-semibold">Integratori consigliati</h3>
          <p className="text-muted-foreground max-w-md mx-auto">Suggerimenti su misura per il tuo obiettivo e le tue analisi, con dosaggi e timing.</p>
          <Button onClick={generate} disabled={generating} className="btn-brand border-0 rounded-full" data-testid="generate-supplements-button">
            <Sparkles className="h-4 w-4 mr-2" /> {generating ? "Generazione…" : "Genera consigli"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="supplements-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Integratori consigliati</h2>
          <p className="text-muted-foreground text-sm">Basati su profilo, obiettivo e ultime analisi.</p>
        </div>
        <Button variant="outline" onClick={premium ? generate : openUpgrade} disabled={generating} data-testid="regenerate-supplements-button">
          {premium ? <RefreshCw className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />} Aggiorna
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {(data.supplements || []).map((s, i) => (
          <Card key={i} className="rounded-2xl card-lift" data-testid={`supplement-${i}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-lg font-semibold">{s.name}</h3>
                <Badge className={PRIORITY[s.priority] || "bg-secondary"}>{s.priority}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">{s.reason}</p>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5 font-mono-data"><Pill className="h-4 w-4 text-primary" /> {s.dosage}</span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Clock className="h-4 w-4" /> {s.timing}</span>
              </div>
              {s.meal && (
                <Badge variant="outline" className="border-primary/30 text-primary gap-1.5" data-testid={`supplement-meal-${i}`}>
                  <Utensils className="h-3.5 w-3.5" /> Assumere a: {s.meal}
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {!premium && data.locked_count > 0 && (
        <PremiumLock testid="premium-lock-overlay-supplements" title={`Altri ${data.locked_count} integratori pensati per te`}
          description="DietFlix ha preparato il tuo protocollo completo, con dosaggi, orari e abbinamento ai pasti della dieta."
          cta="Sblocca il protocollo completo">
          <div className="grid sm:grid-cols-2 gap-4 p-4">
            {[...Array(Math.min(data.locked_count, 4))].map((_, i) => (
              <div key={i} className="rounded-2xl border bg-card p-5 space-y-3">
                <div className="h-5 w-2/3 rounded bg-secondary" />
                <div className="h-3 w-full rounded bg-secondary" />
                <div className="h-3 w-4/5 rounded bg-secondary" />
                <div className="h-6 w-1/2 rounded-full bg-primary/15" />
              </div>
            ))}
          </div>
        </PremiumLock>
      )}

      {data.disclaimer && (
        <div className="flex gap-3 rounded-xl border border-accent/40 bg-accent/5 p-4 text-sm" data-testid="supplements-disclaimer">
          <AlertTriangle className="h-5 w-5 text-accent shrink-0" />
          <p className="text-muted-foreground">{data.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
